// Chiptune instruments (docs/AUDIO_DESIGN.md §12), used only by chiptune.js: the NES's four channels through a warm tape.
// Band-limited pulse PeriodicWaves (12.5 / 25 / 50 % duty, Lanczos-smoothed, the browser culls every partial above
// Nyquist) for the chords, the lead and the rim blip; the 2A03's 4-bit stepped triangle for the bass, the kick and the fill
// toms; 15-bit LFSR noise (long mode, held 2-3 samples: a grainy clock) for the snare, white noise for the hats. The
// chords allocate 1 oscillator + 1 gain a note (a held chord is one voice arpeggiating itself, like a chip channel); bass,
// kit and lead are persistent and gated with setTargetAtTime (a note-on cancels what the last note left scheduled).
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent as persist } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const CHIP_LEVELS = { chords: 1, bass: 1, kit: 1, lead: 1 };
/** At the same RMS a thinner pulse sounds louder and brighter (more of its energy in the upper partials): its trim. */
const DUTY_TRIM = { 0.125: 0.8, 0.25: 0.9, 0.5: 1 };

const cache = new WeakMap();                                         // ac → Map(key → PeriodicWave | AudioBuffer)
const cached = (ac, key, make) => {
  let m = cache.get(ac);
  if (!m) cache.set(ac, (m = new Map()));
  if (!m.has(key)) m.set(key, make());
  return m.get(key);
};
const cents = c => Math.pow(2, c / 1200);
/** the 2A03's 4-bit volume: a velocity snapped to 15 steps (never 0) */
const vol4 = v => Math.max(1, Math.round(clamp(v, 0, 1) * 15)) / 15;

/* ---------------- waves ---------------- */
/** A pulse of duty d: cosine partials a_n = (2/(nπ))·sin(nπd), n = 1..N, each × the Lanczos σ_n = sinc(n/(N+1)) (the edges
 *  barely ring), scaled to RMS .5 whatever the duty (a 12.5 % pulse carries 2.3× less energy than a square), no DC. The browser
 *  band-limits a PeriodicWave to the note it plays, so nothing aliases. */
export function pulseWave(ac, duty, N = 40) {
  return cached(ac, `pulse:${duty}:${N}`, () => {
    const re = new Float32Array(N + 1), im = new Float32Array(N + 1);
    let ss = 0;
    for (let n = 1; n <= N; n++) {
      const x = Math.PI * n / (N + 1);
      re[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * duty) * (Math.sin(x) / x);
      ss += re[n] * re[n] / 2;
    }
    const g = 0.5 / Math.sqrt(ss);
    for (let n = 1; n <= N; n++) re[n] *= g;
    return ac.createPeriodicWave(re, im, { disableNormalization: true });
  });
}
/** The 2A03 triangle: a 32-step staircase (15 … 0, 0 … 15), its exact Fourier series to harmonic 72 (each step integrated:
 *  the steps are the soft buzz of harmonics 31/33 and 63/65 the real channel has, 30 and 36 dB under the fundamental),
 *  peak 1 (the browser's normalisation). */
export function triWave(ac, N = 72) {
  return cached(ac, `tri:${N}`, () => {
    const re = new Float32Array(N + 1), im = new Float32Array(N + 1);
    for (let n = 1; n <= N; n++) {
      const w = 2 * Math.PI * n / 32;
      let a = 0, b = 0;
      for (let k = 0; k < 32; k++) {
        const s = (k < 16 ? 15 - k : k - 16) / 7.5 - 1;
        a += s * (Math.sin(w * (k + 1)) - Math.sin(w * k)); b += s * (Math.cos(w * k) - Math.cos(w * (k + 1)));
      }
      re[n] = a / (Math.PI * n); im[n] = b / (Math.PI * n);
    }
    return ac.createPeriodicWave(re, im);
  });
}
/** 2A03 noise, long mode: a 15-bit LFSR (feedback bit 0 ⊕ bit 1) held for `hold` samples (the channel's clock is sr / hold),
 *  ±.2 (RMS .2 like dsp.js noise), a 1.2 s loop (built on the first track that needs it: ~1 ms). Held 2–3 samples it is the grainy snare (held 1 it would be white noise). */
function lfsr(ac, hold) {
  return cached(ac, `lfsr:${hold}`, () => {
    const sr = ac.sampleRate, n = Math.round(1.2 * sr), buf = ac.createBuffer(1, n, sr), d = buf.getChannelData(0);
    let reg = 1, v = 0;
    for (let i = 0; i < n; i++) {
      if (i % hold === 0) { reg = (reg >> 1) | (((reg ^ (reg >> 1)) & 1) << 14); v = reg & 1 ? 0.2 : -0.2; }
      d[i] = v;
    }
    return buf;
  });
}
const wosc = (ac, wave, f) => { const o = osc(ac, 'sine', f); o.setPeriodicWave(wave); return o; };

/* ---------------- chords: pulse channels arpeggiating, with an echo channel ---------------- */
/** p = plan.chip.chords: { duty, arp (steps a note in a held chord's own arpeggio: .5 = 32nds), shape ('up' | 'updown' |
 *  'down'), decay (τ s of a blip's fall), sus (a roll's sustain), echo: { steps, wet, pan } }; lp = the chord LP (plan.ep.lp).
 *  The event sets the articulation: one note (a comping cell's pick) = a blip; several notes ≤ 2.2 steps = a stab (all at
 *  once, each 1/√n); several held longer = a roll, one voice stepping through them every p.arp steps (the chip way to play a
 *  chord on one channel), which a new chord cuts (10 ms). Level .86 × DUTY_TRIM, the velocity 4-bit (vol4). Bus: LP (Q .6) → pan −.12 → dry, reverb
 *  .22, and the echo channel: a copy p.echo.steps 16ths later → LP 1.8 kHz (feedback .18) → wet p.echo.wet at pan p.echo.pan. */
export function createChipChords(ac, out, send, p, lp, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, stepDur, log = null }) {
  const wave = pulseWave(ac, p.duty), busIn = gain(ac, 1), lpf = filter(ac, 'lowpass', lp, 0.6), pan = panner(ac, -0.12), sendG = gain(ac, 0.22);
  const dly = ac.createDelay(1), dLP = filter(ac, 'lowpass', 1800, 0.5), fb = gain(ac, 0.18), wetG = gain(ac, p.echo.wet), ePan = panner(ac, p.echo.pan);
  dly.delayTime.value = Math.min(0.95, p.echo.steps * stepDur);
  busIn.connect(lpf); lpf.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  lpf.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wetG); wetG.connect(ePan); ePan.connect(out);
  const bus = persist(registry, tag, [], [busIn, lpf, pan, sendG, dly, dLP, fb, wetG, ePan], t0);
  const L = 0.86 * (DUTY_TRIM[p.duty] ?? 1) * CHIP_LEVELS.chords, step = p.arp * stepDur;
  let voices = [], roll = null, maxActive = 0, steals = 0, notes = 0;   // { v, midi, end (sounding end), rec }
  const cut = (x, t) => { registry.stop(x.v, t, 0.01); x.end = t; if (x.rec) x.rec[1] = t; };

  /** one voice: a pulse oscillator (±1.5 ¢) → amp. env(amp.gain, o.frequency) schedules it and returns its sounding end. */
  function voice(t, midi, env) {
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) { const old = voices.shift(); if (old === roll) roll = null; cut(old, t); steals++; }
    const o = wosc(ac, wave, mtof(midi) * cents(r() * 3 - 1.5)), amp = gain(ac, 0);
    o.connect(amp); amp.connect(busIn);
    const end = env(amp.gain, o.frequency);
    o.start(t); o.stop(end + 0.3);
    const v = registry.add({ src: [o], all: [o, amp], start: t, end: end + 0.3, tag, out: amp });
    const x = { v, midi, end, rec: log ? [t, end] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
    return x;
  }
  /** a blip: attack τ 2.5 ms to A, falling to .35 A (τ p.decay), release τ max(40 ms, .6·rel) */
  const blip = (t, midi, A, dur, rel) => {
    const k = voices.findIndex(x => x.midi === midi && x !== roll);
    if (k >= 0) cut(voices.splice(k, 1)[0], t);                          // the same key again: its old voice lets go
    return voice(t, midi, g => {
      const off = t + Math.max(0.04, dur), tau = Math.max(0.04, 0.6 * rel);
      g.setValueAtTime(0, t); g.setTargetAtTime(A, t, 0.0025); g.setTargetAtTime(0.35 * A, t + 0.012, p.decay);
      g.setTargetAtTime(0, off, tau);
      return off + tau * 5;
    });
  };
  /** a roll: the chord's notes in p.shape order, one every p.arp steps (through the release too); attack τ 3 ms, down to
   *  p.sus (τ .6 s), release τ max(60 ms, rel) */
  const arpRoll = (t, midis, A, dur, rel) => {
    const n = midis.length, order = p.shape === 'down' ? [...midis].reverse()
      : p.shape === 'updown' && n > 2 ? [...midis, ...midis.slice(1, -1).reverse()] : midis;
    return voice(t, order[0], (g, fr) => {
      const off = t + Math.max(0.05, dur), tau = Math.max(0.06, rel), end = off + tau * 5, f = order.map(m => mtof(m) * cents(r() * 3 - 1.5));
      for (let k = 0, tk = t; tk < end && k < 600; k++, tk = t + k * step) fr.setValueAtTime(f[k % order.length], tk);
      g.setValueAtTime(0, t); g.setTargetAtTime(A, t, 0.003); g.setTargetAtTime(p.sus * A, t + 0.02, 0.6);
      g.setTargetAtTime(0, off, tau);
      return end;
    });
  };
  return {
    chord(t, midis, vel, dur, strum = 0.003, rel = 0.08) {
      const n = midis.length, A = L * vol4(vel);
      if (roll && roll.end > t) { const i = voices.indexOf(roll); if (i >= 0) voices.splice(i, 1); cut(roll, t); }
      roll = null;
      if (n === 1) blip(t, midis[0], A, dur, rel);
      else if (dur <= 2.2 * stepDur) midis.forEach((m, k) => blip(t + (k * strum) / (n - 1), m, (A / Math.sqrt(n)) * (k === n - 1 ? 1.08 : 1), dur, rel));
      else roll = arpRoll(t, midis, A, dur, rel);
    },
    stats: () => ({ maxActive, steals, notes, active: voices.filter(x => x.end > ac.currentTime).length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- bass: the stepped triangle (mono, persistent) ---------------- */
/** p = plan.chip.bass: { lp }. Triangle → LP p.lp (Q .5: the steps' buzz stays soft) → amp. The 2A03 triangle has no volume
 *  control, so the dynamics are faked gently: .23·(.8 + .2v); attack τ 2 ms, a slight sag to .9 (τ .6 s), release τ 12 ms
 *  (short, like the channel's gate); slides glide τ 30 ms. */
export function createTriBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const o = wosc(ac, triWave(ac), 55), lpf = filter(ac, 'lowpass', p.lp, 0.5), amp = gain(ac, 0);
  o.connect(lpf); lpf.connect(amp); amp.connect(out);
  const P = persist(registry, tag, [o], [o, lpf, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.23 * (0.8 + 0.2 * vel) * CHIP_LEVELS.bass;
      hold(o.frequency, t); hold(amp.gain, t);
      if (slide) o.frequency.setTargetAtTime(f, t, 0.03); else o.frequency.setValueAtTime(f, t);
      amp.gain.setTargetAtTime(A, t, 0.002); amp.gain.setTargetAtTime(0.9 * A, t + 0.02, 0.6);
      amp.gain.setTargetAtTime(0, t + Math.max(0.05, dur), 0.012);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- kit: triangle kick, noise snare / hats / shaker, pulse rim blip, triangle toms ---------------- */
/** kit = plan.drums.kit: { kickF0, kickF1, kickSweep, kickDecay, snareBP, snareHold, snareDecay, snareBody, hatHP, hatClosed,
 *  hatOpen, rimF, lp }. White noise (dsp.js, shared with the ambience) feeds the hats, shaker and kick tick, the LFSR held
 *  kit.snareHold samples the snare; bus LP kit.lp. The tom reads its
 *  step from the event: steps 10…15 run 260 Hz down 2 semitones a step, each hit sweeping to .6 of it. */
export function createChipKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, stepDur }) {
  const L = LEVELS.kit * CHIP_LEVELS.kit, nodes = [], N = x => (nodes.push(x), x), tri = triWave(ac);
  const hi = N(ac.createBufferSource()), lo = N(ac.createBufferSource());
  hi.buffer = noise(ac, 'white'); lo.buffer = lfsr(ac, kit.snareHold); hi.loop = lo.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  /** a gated voice: amp → pan (null: straight to the bus) → bus, plus a reverb send */
  const voice = (pan, rev = 0) => {
    const a = N(gain(ac, 0)), pn = pan == null ? a : N(panner(ac, pan));
    if (pn !== a) a.connect(pn);
    pn.connect(bus);
    if (rev) { const s = N(gain(ac, rev)); pn.connect(s); s.connect(send); }
    return a;
  };
  // kick: the triangle swept kickF0 (160-200 Hz) → kickF1 (46-54 Hz), τ kickSweep, decay τ kickDecay; a soft tick of noise → LP 2.5 kHz
  const kOsc = N(wosc(ac, tri, kit.kickF1)), kAmp = voice(null), kLP = N(filter(ac, 'lowpass', 2500, 0.7)), kTick = voice(null);
  kOsc.connect(kAmp); hi.connect(kLP); kLP.connect(kTick);
  // snare: the grainy noise → BP snareBP (Q .8), τ snareDecay (ghost 30 ms), + a 25 % pulse body snareBody → .7 of it (τ 25 ms)
  // → LP 1.4 kHz, τ 30 ms; pan −.05, reverb .22
  const sBP = N(filter(ac, 'bandpass', kit.snareBP, 0.8)), sAmp = voice(-0.05, 0.22);
  const sOsc = N(wosc(ac, pulseWave(ac, 0.25, 24), kit.snareBody)), sLP = N(filter(ac, 'lowpass', 1400, 0.5)), sTone = voice(-0.05, 0.22);
  lo.connect(sBP); sBP.connect(sAmp); sOsc.connect(sLP); sLP.connect(sTone);
  // hats: noise → HP hatHP; closed τ hatClosed, open τ hatOpen on one gain (a closed hit chokes an open one); pan +.22, reverb .06
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = voice(0.22, 0.06);
  hi.connect(hHP); hHP.connect(hAmp);
  // shaker: noise → HP 5 kHz → BP 8 kHz, attack τ 3 ms, τ 22 ms; pan −.25
  const xHP = N(filter(ac, 'highpass', 5000, 0.7)), xBP = N(filter(ac, 'bandpass', 8000, 0.8)), xAmp = voice(-0.25);
  hi.connect(xHP); xHP.connect(xBP); xBP.connect(xAmp);
  // rim: a 12.5 % pulse blip at rimF dropping to .8 of it (τ 10 ms) → HP 400, τ 9 ms; pan −.15, reverb .25
  const rOsc = N(wosc(ac, pulseWave(ac, 0.125, 24), kit.rimF)), rHP = N(filter(ac, 'highpass', 400, 0.7)), rAmp = voice(-0.15, 0.25);
  rOsc.connect(rHP); rHP.connect(rAmp);
  // tom (fills): the triangle, decay τ .18 s; pan +.3 (high) → −.3 (low), reverb .15
  const tOsc = N(wosc(ac, tri, 200)), tAmp = N(gain(ac, 0)), tPan = N(panner(ac, 0)), tSend = N(gain(ac, 0.15));
  tOsc.connect(tAmp); tAmp.connect(tPan); tPan.connect(bus); tPan.connect(tSend); tSend.connect(send);
  const P = persist(registry, tag, [hi, lo, kOsc, sOsc, rOsc, tOsc], nodes, t0);
  const hit = (prm, peak, t, atk, rel, tau) => { prm.setTargetAtTime(peak, t, atk); prm.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, kit.kickSweep);
      hit(kAmp.gain, 0.85 * v * L, t, 0.0012, 0.006, kit.kickDecay);
      hit(kTick.gain, 0.3 * v * L, t, 0.0005, 0.0015, 0.003);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(kit.snareBody, t); sOsc.frequency.setTargetAtTime(0.7 * kit.snareBody, t, 0.025);
      hit(sAmp.gain, 0.6 * v * L, t, 0.001, 0.004, ghost ? 0.03 : kit.snareDecay);
      hit(sTone.gain, 0.16 * v * L, t, 0.001, 0.004, 0.03);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.24 * v * L, t, 0.0008, 0.003, open ? kit.hatOpen : kit.hatClosed); },
    shaker(t, v) { hit(xAmp.gain, 0.22 * v * L, t, 0.003, 0.006, 0.022); },
    rim(t, v) {
      rOsc.frequency.setValueAtTime(kit.rimF, t); rOsc.frequency.setTargetAtTime(0.8 * kit.rimF, t, 0.01);
      hit(rAmp.gain, 0.1 * v * L, t, 0.0008, 0.002, 0.009);
    },
    tom(t, v, e) {
      const k = e ? clamp(Math.round(e.t / stepDur) % 16, 10, 15) - 10 : 0, f0 = 260 * Math.pow(2, -k / 6);
      tOsc.frequency.setValueAtTime(f0, t); tOsc.frequency.setTargetAtTime(0.6 * f0, t, 0.05);
      tPan.pan.setValueAtTime(0.3 - 0.12 * k, t);
      hit(tAmp.gain, 0.42 * v * L, t, 0.001, 0.005, 0.18);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lead: a pulse channel with delayed vibrato, slides and an echo channel ---------------- */
/** lead = plan.lead ({ pan }), p = plan.chip.lead: { duty, lp, vib (¢), vibRate, vibDelay (s), bend (odds), slide (odds), glide
 *  (τ s), echo (steps), fb, wet }. Pulse → LP p.lp → amp → pan; the echo channel is a quieter copy p.echo 16ths later (LP 2 kHz,
 *  feedback p.fb, wet p.wet) on the other side. A note: attack τ 4 ms to .4v (× DUTY_TRIM), down to .7 (τ .4 s), release τ 60 ms; the
 *  same pitch again dips to .3 first (a re-trigger). A legato step of ≤ 4 semitones slides (portamento τ p.glide) with odds
 *  p.slide, a fresh note bends up from a tone or a semitone below (τ 35 ms) with odds p.bend. Vibrato from p.vibDelay into
 *  notes longer than .28 s (τ .12 s in). */
export function createChipLead(ac, out, send, lead, p, { registry, tag, stepDur, r = Math.random, t0 = ac.currentTime }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const o = N(wosc(ac, pulseWave(ac, p.duty), 440)), lp = N(filter(ac, 'lowpass', p.lp, 0.5)), amp = N(gain(ac, 0));
  const pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.3)), vib = N(osc(ac, 'sine', p.vibRate)), vibG = N(gain(ac, 0));
  const dly = N(ac.createDelay(1)), dLP = N(filter(ac, 'lowpass', 2000, 0.5)), fb = N(gain(ac, p.fb)), wet = N(gain(ac, p.wet));
  const ePan = N(panner(ac, (lead.pan || 0) > 0 ? -0.45 : 0.45));
  dly.delayTime.value = Math.min(0.95, p.echo * stepDur);
  o.connect(lp); lp.connect(amp); amp.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(ePan); ePan.connect(out);
  vib.connect(vibG); vibG.connect(o.detune);
  const P = persist(registry, tag, [o, vib], nodes, t0);
  const Lv = (DUTY_TRIM[p.duty] ?? 1) * CHIP_LEVELS.lead;
  let lastF = 0, lastEnd = -1;
  return {
    note(t, midi, vel, dur, glide = false) {
      const d = Math.max(0.06, dur), f = mtof(midi), A = 0.4 * vol4(vel) * Lv;
      const slide = glide && lastF && Math.abs(12 * Math.log2(f / lastF)) <= 4 && r() < p.slide, same = lastF && Math.abs(f / lastF - 1) < 0.01 && t < lastEnd + 0.05;
      lastF = f; lastEnd = t + d;
      for (const prm of [o.frequency, amp.gain, vibG.gain]) hold(prm, t);
      if (slide) o.frequency.setTargetAtTime(f, t, p.glide);
      else if (!glide && r() < p.bend) { o.frequency.setValueAtTime(f * cents(r() < 0.6 ? -200 : -100), t); o.frequency.setTargetAtTime(f, t + 0.01, 0.035); }
      else o.frequency.setValueAtTime(f, t);
      if (slide) amp.gain.setTargetAtTime(A, t, 0.02);
      else if (same) { amp.gain.setTargetAtTime(0.3 * A, t, 0.004); amp.gain.setTargetAtTime(A, t + 0.014, 0.004); }
      else amp.gain.setTargetAtTime(A, t, 0.004);
      amp.gain.setTargetAtTime(0.7 * A, t + 0.05, 0.4);
      amp.gain.setTargetAtTime(0, t + d, 0.06);
      vibG.gain.setTargetAtTime(0, t, 0.02);
      if (d > 0.28) vibG.gain.setTargetAtTime(p.vib, t + p.vibDelay, 0.12);
    },
    dispose(t) { P.dispose(t); },
  };
}
